import React, { useMemo } from "react";
import PropTypes from "prop-types";
import GutenbergBlock from "./gutenberg-block";

/**
 * Build a block tree from a flat editorBlocks list.
 * WPGraphQL returns a flat array with clientId / parentClientId references.
 * gatsby-source-wordpress may not populate innerBlocks beyond level 1,
 * so we reconstruct the tree here.
 */
function buildBlockTree(flatBlocks) {
  const safeBlocks = flatBlocks?.filter(Boolean) || [];

  if (!safeBlocks.length) return [];

  // If the first block has no clientId, the list is already structured — return as-is.
  if (!safeBlocks[0]?.clientId) return safeBlocks;

  const blockMap = new Map();

  safeBlocks.forEach((block) => {
    if (!block?.clientId) return;
    blockMap.set(block.clientId, { ...block, innerBlocks: [] });
  });

  const roots = [];

  safeBlocks.forEach((block) => {
    if (!block?.clientId) return;

    const node = blockMap.get(block.clientId);

    if (!node) return;

    if (block.parentClientId && blockMap.has(block.parentClientId)) {
      blockMap.get(block.parentClientId).innerBlocks.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/* How many leading blocks count as "above the fold". They skip the scroll-reveal
 * animation (which keeps content at opacity 0 until JS has run) and load their
 * images eagerly. Spacers don't count: a leading spacer would otherwise use up
 * the exemption and leave the first real block hidden until hydration. */
const ABOVE_FOLD_BLOCK_COUNT = 2;
const NON_VISUAL_BLOCKS = ["core/spacer"];

const GutenbergBlocks = ({
  blocks,
  isTopLevel,
  aboveFold,
  disableAboveFold,
}) => {
  const currentBlocks = useMemo(() => {
    const safeBlocks = blocks?.filter(Boolean) || [];

    if (isTopLevel) {
      return buildBlockTree(safeBlocks);
    }

    return safeBlocks;
  }, [blocks, isTopLevel]);

  const aboveFoldBlocks = useMemo(() => {
    const result = new Set();
    if (disableAboveFold || !(isTopLevel || aboveFold)) {
      return result;
    }
    for (const block of currentBlocks) {
      if (result.size >= ABOVE_FOLD_BLOCK_COUNT) break;
      if (!NON_VISUAL_BLOCKS.includes(block?.name)) result.add(block);
    }
    return result;
  }, [currentBlocks, isTopLevel, aboveFold, disableAboveFold]);

  /* Group similar blocks together */
  const chunks =
    currentBlocks?.reduce((chunks, block) => {
      const lastChunk = chunks.slice(-1)?.[0];
      if (block?.name === lastChunk?.wrapper) {
        lastChunk.blocks.push(block);
        return chunks;
      } else {
        return [
          ...chunks,
          {
            wrapper: block?.name,
            blocks: [block],
          },
        ];
      }
    }, []) || [];

  return (
    <>
      {chunks.map(({ wrapper, blocks }, i) => {
        switch (wrapper) {
          case "mev/bus-card":
            return (
              <div
                key={i}
                className="bus-card-container"
                style={{ justifyContent: "flex-start" }}
              >
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          case "mev/card":
            return (
              <div
                key={i}
                className="row-container"
                style={{ justifyContent: "flex-start" }}
              >
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );

          case "mev/employee-card":
            return (
              <div key={i} className="employee-card-container">
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          case "mev/faq":
            return (
              <div
                key={i}
                className="faq-container"
                style={{ justifyContent: "flex-start" }}
              >
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          case "mev/info-card":
            return (
              <div key={i} className="info-cards-container">
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          case "mev/metric":
            return (
              <div
                key={i}
                className="row-container metric-wrapper"
                style={{ margin: 0, justifyContent: "center" }}
              >
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          case "mev/video-card":
            return (
              <div key={i} className="row-container">
                {blocks.map((block, j) => (
                  <GutenbergBlock key={j} block={block} />
                ))}
              </div>
            );
          default:
            return (
              <React.Fragment key={i}>
                {blocks.map((block, j) => (
                  <GutenbergBlock
                    key={j}
                    block={block}
                    aboveFold={aboveFoldBlocks.has(block)}
                  />
                ))}
              </React.Fragment>
            );
        }
      })}
    </>
  );
};

export default GutenbergBlocks;

GutenbergBlocks.propTypes = {
  blocks: PropTypes.array,
  isTopLevel: PropTypes.bool,
  aboveFold: PropTypes.bool,
  disableAboveFold: PropTypes.bool,
};

GutenbergBlocks.defaultProps = {
  isTopLevel: false,
  aboveFold: false,
  disableAboveFold: false,
};

GutenbergBlocks.privateQuery = GutenbergBlock.privateQuery;
